import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import EditorField from '../Editor/editor-field';
import { checkValueType, parseValue, parseNumber } from '../Editor/util';
import EditorLabel from '../Editor/EditorLabel';
import EditorContainer from '../Editor/EditorContainer';
import EditorJSON from '../Editor/EditorJSON';
import ErrorContainer from '../Editor/ErrorContainer';
import { ElidableText, InputV2 } from '..';
import { InputShape } from '../v2/input';

const SPACING = 2;

function AttributeStatic(props) {
  const {
    isEditing, temporaryObject, validateObject, themeMode, setSelectedObject,
  } = props;

  const valueType = checkValueType(temporaryObject.payload);

  const isNumber = valueType === 'number';
  const isBoolean = valueType === 'boolean';
  const isList = valueType === 'list';
  const isCustomJSON = valueType === 'json';
  const isErrored = temporaryObject.errorArray && temporaryObject.errorArray.includes('payload');

  const getPlaceholder = () => (isList ? '"Item 1", "Item 2", etc' : 'Enter a value');

  const selectItem = (item, type) => {
    // Update the temporary object with the new information
    const newObject = { ...temporaryObject };
    if (type === 'number') {
      newObject.payload = parseNumber(item);
    } else {
      newObject.payload = item;
    }
    validateObject(newObject);
  };

  const selectValueType = (type) => {
    switch (type) {
      case 'number':
        selectItem(0, type);
        break;
      case 'boolean':
        selectItem(true, type);
        break;
      case 'list':
        selectItem('[]', type);
        break;
      case 'json':
        selectItem({}, type);
        break;
      default:
        selectItem('', type);
        break;
    }
  };

  const handleTextAreaChange = (event) => {
    // Set an unparsed string in the selectedObject that can be validated on save
    setSelectedObject(event.target.value);
    try {
      const parsedJSON = JSON.parse(event.target.value, undefined, 4);
      selectItem(parsedJSON, valueType);
    } catch (error) {
      // addNotification(error);
    }
  };

  const getPayloadComponent = () => {
    if (isBoolean) {
      return (
        <Box flex={isEditing ? 3 : 1} height="100%">
          <EditorField
            isEditing={isEditing}
            value={temporaryObject.payload}
            selectItem={(item) => selectItem(item, valueType)}
            items={[true, false]}
            noFreeText
            isErrored={isErrored}
          />
        </Box>
      );
    }

    if (isCustomJSON) {
      return (
        <ErrorContainer
          isEditing={isEditing}
          isErrored={isErrored}
        >
          <EditorJSON
            isEditing={isEditing}
            themeMode={themeMode}
            jsonObject={temporaryObject.payload}
            jsonString={JSON.stringify(temporaryObject.payload, undefined, 4)}
            handleChange={handleTextAreaChange}
          />
        </ErrorContainer>
      );
    }

    return isEditing ? (
      <Box height="32px" flex={3}>
        <InputV2 value={parseValue(valueType, temporaryObject.payload)} placeholder={getPlaceholder()} onChange={(event) => selectItem(isList ? `[${event.target.value}]` : event.target.value, valueType)} isErrored={isErrored} testId="editor-text" shape={InputShape.SQUARE} />
      </Box>
    ) : (
      <Text variant="primary" flex={1} data-testid="editor-text" m={2} height="100%">
        <ElidableText text={parseValue(valueType, temporaryObject.payload)} lineCount={1} />
      </Text>
    );
  };

  return (
    <EditorContainer variant="inner" p={3} mt={3} data-testid="editor-value">
      <EditorLabel>Value</EditorLabel>
      <Flex alignItems="flex-start">
        {isEditing && (
          <Box mr={SPACING} minWidth={150}>
            <EditorField
              isEditing
              value={valueType}
              selectItem={selectValueType}
              items={['string', 'number', 'boolean', 'list', 'json']}
              noFreeText
            />
          </Box>
        )}
        {getPayloadComponent()}
      </Flex>
    </EditorContainer>
  );
}

AttributeStatic.propTypes = {
  isEditing: PropTypes.bool.isRequired,
  temporaryObject: PropTypes.object.isRequired,
  validateObject: PropTypes.func.isRequired,
  themeMode: PropTypes.string.isRequired,
  setSelectedObject: PropTypes.func.isRequired,
};

export default AttributeStatic;
